<template>
    <div class="hidden lg:block bg-zinc-900 border-b border-zinc-800">
        <div class="max-w-[1400px] mx-auto px-6 py-12">
            <div class="flex items-start justify-between gap-10">
                <div class="flex-1 min-w-0">
                    <h1 class="text-5xl font-bold text-white tracking-tight">
                        {{ heroTitle }}
                    </h1>

                    <!-- View: All -->
                    <template v-if="view === 'all'">
                        <p v-if="!isUserLoggedIn" class="mt-4 text-xl text-white/70 max-w-3xl leading-relaxed">
                            Members sponsor auditions to move performers toward production. When an audition is fully sponsored, it becomes an official release.
                        </p>
                        <p v-else class="mt-4 text-xl text-white/70 max-w-2xl leading-relaxed">
                            Every audition you support moves a performer closer to production. Fully sponsored auditions are produced as official releases.
                        </p>

                        <div class="mt-6 flex flex-wrap items-center gap-6 text-sm text-white/55">
                            <div class="flex items-center gap-2">
                                <div class="w-1.5 h-1.5 rounded-full bg-green-500" />
                                <span>
                                    <span class="text-white/90 font-semibold">{{ formatStars(totalStarsGiven) }}</span> stars given
                                </span>
                            </div>

                            <div class="h-4 w-px bg-white/10" />

                            <div class="flex items-center gap-2">
                                <TrendingUp class="w-4 h-4 text-white/45" />
                                <span>
                                    <span class="text-white/90 font-semibold">{{ sponsoredLast30Days }}</span> sponsored last 30 days
                                </span>
                            </div>
                        </div>

                        <!-- Top Sponsors - Dedicated Line on Desktop -->
                        <div v-if="topSponsors.length > 0" class="mt-4 flex items-center gap-3">
                            <div class="flex items-center gap-2 flex-shrink-0">
                                <Trophy class="w-4 h-4 text-yellow-500" />
                                <span class="text-white/60 text-sm font-semibold whitespace-nowrap">Top Sponsors:</span>
                            </div>

                            <button
                                @click="$emit('openLeaderboard')"
                                class="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/10 hover:border-white/20 hover:bg-white/5 transition group min-w-0"
                            >
                                <div class="flex items-center gap-3 text-sm min-w-0">
                                    <div v-for="(sponsor, index) in topSponsors.slice(0, maxVisibleTopSponsors)"
                                         :key="sponsor.user_id"
                                         class="flex items-center gap-1.5 flex-shrink-0">
                                        <span class="text-white/50">#{{ sponsor.rank }}</span>
                                        <span class="text-white font-semibold">{{ sponsor.sponsor_name }}</span>
                                        <span class="text-white/70">{{ sponsor.total_stars }}</span>
                                        <img src="https://sm.naughtycdn.com/images/landers/prize/NA-Star.png" alt="" class="w-3.5 h-3.5" />
                                        <span v-if="index < maxVisibleTopSponsors - 1" class="text-white/30">•</span>
                                    </div>
                                </div>
                                <span class="text-white/30 group-hover:text-white/50 transition text-sm ml-1 flex-shrink-0">→</span>
                            </button>
                        </div>
                    </template>

                    <!-- View: Mine -->
                    <template v-else-if="view === 'mine'">
                        <p class="mt-4 text-xl text-white/65 max-w-2xl leading-relaxed">
                            Track what you've sponsored and see which auditions are
                            closest to becoming full scenes.
                        </p>

                        <div class="mt-6 flex flex-wrap items-center gap-6 text-sm text-white/55">
                            <span>
                                You've sponsored
                                <span class="text-white/90 font-semibold">
                                  {{ userSponsoredCount }}
                                </span>
                                auditions
                            </span>
                            <div class="h-4 w-px bg-white/10" />
                            <span>
                                Total stars given:
                                <span class="text-white/90 font-semibold">
                                    {{ userTotalStarsGiven }}
                                </span>
                            </span>
                        </div>
                    </template>

                    <!-- View: Sponsored -->
                    <template v-else-if="view === 'sponsored'">
                        <p class="mt-4 text-xl text-white/65 max-w-2xl leading-relaxed">
                            Auditions that have received star support from members, working their way toward a full production.
                        </p>

                        <div class="mt-6 flex flex-wrap items-center gap-6 text-sm text-white/55">
                            <span>
                                <span class="text-white/90 font-semibold">{{ totalSponsored }}</span>
                                auditions sponsored
                            </span>
                            <div class="h-4 w-px bg-white/10" />
                            <div class="flex items-center gap-2">
                                <div class="w-1.5 h-1.5 rounded-full bg-green-500" />
                                <span>
                                    <span class="text-white/90 font-semibold">{{ formatStars(sponsoredTotalStarsGiven) }}</span> stars given
                                </span>
                            </div>
                        </div>
                    </template>

                    <!-- View: Released -->
                    <template v-else>
                        <p class="mt-4 text-xl text-white/65 max-w-2xl leading-relaxed">
                            Watch the full scenes made possible by member sponsorships.
                        </p>

                        <div class="mt-6 flex flex-wrap items-center gap-6 text-sm text-white/55">
                            <span>
                                <span class="text-white/90 font-semibold">
                                    {{ releasedCount }}
                                </span>
                                scenes released
                            </span>
                            <div class="h-4 w-px bg-white/10" />
                            <span>
                                <span class="text-white/90 font-semibold">{{ sponsoredThisMonth }}</span>
                                funded this month
                            </span>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>

    <div ref="heroRef">
        <MobileHero
            :view="view"
            :isUserLoggedIn="isUserLoggedIn || false"
            :sponsoredThisMonth="sponsoredThisMonth"
            :sponsoredLast30Days="sponsoredLast30Days"
            :totalSponsored="totalSponsored"
            :sponsoredTotalStarsGiven="sponsoredTotalStarsGiven"
            :totalStarsGiven="totalStarsGiven"
            :userSponsoredCount="userSponsoredCount"
            :releasedCount="releasedCount"
            :userTotalStarsGiven="userTotalStarsGiven"
            :topSponsors="topSponsors"
            @joinClick="$emit('joinClick')"
            @openLeaderboard="$emit('openLeaderboard')"
        />
    </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { TrendingUp, Trophy } from 'lucide-vue-next'
import MobileHero from "@/Components/Reels/MobileHero.vue";

const props = defineProps({
    view: {
        type: String,
        required: true,
        validator: (value) => ['all', 'mine', 'released', 'sponsored'].includes(value)
    },
    isUserLoggedIn: {
        type: Boolean,
        required: true
    },
    sponsoredThisMonth: {
        type: Number,
        required: true
    },
    sponsoredLast30Days: {
        type: Number,
        required: true
    },
    totalSponsored: {
        type: Number,
        required: true
    },
    sponsoredTotalStarsGiven: {
        type: Number,
        required: true
    },
    totalStarsGiven: {
        type: Number,
        required: true
    },
    userSponsoredCount: {
        type: Number,
        required: true
    },
    releasedCount: {
        type: Number,
        required: true
    },
    userTotalStarsGiven: {
        type: Number,
        required: true
    },
    topSponsors: {
        type: Array,
        default: []
    },
})

defineEmits(['openLeaderboard', 'joinClick'])

const heroRef = ref(null)
const maxVisibleTopSponsors = ref(3)

const heroTitle = computed(() => {
    if (props.view === 'mine') return 'My Sponsorships'
    if (props.view === 'released') return 'Sponsored & Released'
    if (props.view === 'sponsored') return 'Community-Backed Auditions'
    return 'Your Stars Unlock Fantasies'
})

const formatStars = (num) => {
    if (num >= 1000000) {
        return `${(num / 1000000).toFixed(1)}M+`;
    }
    if (num >= 100000) {
        return `${Math.floor(num / 1000)}K+`;
    }
    return num.toLocaleString()
}
</script>
